import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ShieldCheck, Wrench, User, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import XpertLogo, { XpertWordmark } from '../components/XpertLogo';
import { auth } from '../lib/api';
import { useConfig } from '../context/ConfigContext';
import { toast } from 'sonner';
import { useLang } from '../lib/i18n';
import LanguageToggle from '../components/LanguageToggle';

const roles = [
  { id: 'admin', label: 'Admin', icon: ShieldCheck, color: 'from-[#8BC53F] to-[#6FA82C]', email: 'info@xpertpestssolutions.com', dest: '/app/dashboard' },
  { id: 'tech', label: 'Technician', icon: Wrench, color: 'from-[#F59E0B] to-[#D4870A]', email: 'ish@xpertpestssolutions.com', dest: '/tech' },
  { id: 'customer', label: 'Customer', icon: User, color: 'from-slate-700 to-slate-900', email: 'customer@xpertpestssolutions.com', dest: '/portal' },
];

export default function Login() {
  const { demoMode } = useConfig();
  const { t } = useLang();
  const roleLabel = (id) => t('role.' + id);
  const [role, setRole] = useState('admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Only pre-fill demo credentials when the app is running in demo mode.
  useEffect(() => {
    if (demoMode) { setEmail('info@xpertpestssolutions.com'); setPassword('demo1234'); }
  }, [demoMode]);

  const switchRole = (r) => {
    setRole(r.id);
    if (demoMode) { setEmail(r.email); setPassword('demo1234'); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error(t('login.enterBoth')); return; }
    setLoading(true);
    try {
      const res = await auth.login(email, password, role);
      localStorage.setItem('xp_token', res.access_token);
      localStorage.setItem('pp_user', JSON.stringify(res.user));
      toast.success(t('login.welcome', { name: res.user.name }));
      const dest = roles.find(r => r.id === res.user.role)?.dest || '/app/dashboard';
      setTimeout(() => navigate(dest), 350);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (err?.response?.status === 401 ? t('login.invalid') : t('login.failed'));
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex flex-1 pp-hero-bg relative overflow-hidden text-white p-12">
        <div className="absolute inset-0 pp-dot-grid opacity-30"/>
        <div className="absolute -right-24 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pp-wave"/>
        <div className="relative z-10 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2.5">
            <XpertLogo size={44} light/><XpertWordmark light size="md"/>
          </Link>
          <div className="max-w-md">
            <h2 className="text-4xl font-extrabold tracking-tight leading-tight">One platform. Three workspaces. Built for pest control.</h2>
            <p className="mt-4 text-white/80 leading-relaxed">Admins manage operations. Technicians run their day in the field. Customers self-serve through the portal — all from a single source of truth.</p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {roles.map(r => (
                <div key={r.id} className={`bg-white/10 backdrop-blur rounded-xl p-3 border ${role===r.id?'border-[#F59E0B]':'border-white/10'} text-center`}>
                  <r.icon className="w-6 h-6 mx-auto text-[#F59E0B] mb-1"/>
                  <div className="text-xs font-semibold">{roleLabel(r.id)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-white/50">© {new Date().getFullYear()} Xpertpests Solutions, Inc.</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2.5"><XpertLogo size={40}/><XpertWordmark size="md"/></Link>
          </div>
          <div className="flex justify-end mb-2"><LanguageToggle /></div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{t('login.title')}</h1>
          <p className="text-slate-600 mt-2">{demoMode ? t('login.subtitleDemo') : t('login.subtitle')}</p>
          {demoMode && (
            <div className="mt-4 rounded-lg bg-[#8BC53F]/10 border border-[#8BC53F]/30 px-4 py-3 text-sm text-[#5b8523] font-medium" data-testid="demo-mode-banner">
              🧪 <b>Demo Mode</b> — explore freely with any role. Password: <b>demo1234</b>
            </div>
          )}
          <div className="mt-6 grid grid-cols-3 gap-2">
            {roles.map(r => (
              <button key={r.id} type="button" onClick={()=>switchRole(r)} className={`p-3 rounded-xl border-2 transition text-left ${role===r.id ? 'border-[#8BC53F] bg-[#8BC53F]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${r.color} flex items-center justify-center text-white mb-2`}><r.icon className="w-5 h-5"/></div>
                <div className="text-sm font-bold text-slate-900">{roleLabel(r.id)}</div>
                {demoMode && <div className="text-[10px] text-slate-500 truncate">{r.email}</div>}
              </button>
            ))}
          </div>
          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div><Label htmlFor="email">{t('login.email')}</Label><Input id="email" type="email" value={email} onChange={e=>setEmail(e.target.value)} className="mt-1.5 h-12"/></div>
            <div>
              <div className="flex justify-between items-center"><Label htmlFor="password">{t('login.password')}</Label><a href="#" className="text-sm text-[#8BC53F] font-medium hover:underline">{t('login.forgot')}</a></div>
              <div className="relative mt-1.5">
                <Input id="password" type={show?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)} className="h-12 pr-10"/>
                <button type="button" onClick={()=>setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">{show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}</button>
              </div>
            </div>
            <div className="flex items-center gap-2"><Checkbox id="remember" defaultChecked/><Label htmlFor="remember" className="text-sm cursor-pointer">{t('login.remember')}</Label></div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-[#8BC53F] hover:bg-[#6FA82C] font-bold text-base">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>{t('login.signingIn')}</> : <>{t('login.signInAs', { role: roleLabel(role) })} <ArrowRight className="w-4 h-4 ml-2"/></>}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-600">{t('login.newTo')} <Link to="/pricing" className="text-[#8BC53F] font-semibold hover:underline">{t('login.bookDemo')}</Link></div>
        </div>
      </div>
    </div>
  );
}
